import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useEffect } from 'react';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import StreamIQEntitlementService from '@jwp/ott-common/src/services/integrations/streamiq/StreamIQEntitlementService';

import useEventCallback from './useEventCallback';
import { useWatchHistory } from './useWatchHistory';

// @CUSTOMER vd-ott-iq This hook has been changed to start/stop playback sessions and update the watch history
export default function useProtectedMedia(item: PlaylistItem, streamingProtocol: 'dash' | 'hls' = 'dash', shouldKeepPreviousData = false, trackHistory = true) {
  const entitlementService = getModule(StreamIQEntitlementService);
  const watchHistory = useWatchHistory(trackHistory, item);

  const stopSession = useEventCallback(async () => {
    const watchHistoryItem = useWatchHistoryStore.getState().watchHistory.find((item) => item.mediaid === item.mediaid);
    const positionInMs = watchHistoryItem ? item.duration * watchHistoryItem.progress * 1000 : 0;

    await entitlementService.stopCurrentSession({ positionInMs });
  });

  useEffect(() => {
    return () => {
      void stopSession();
    };
  }, [stopSession]);

  const {
    data: { playableItem, mainContentOffset, contentDuration } = {},
    isLoading,
    error,
  } = useQuery({
    staleTime: 0,
    gcTime: 0,
    queryKey: ['media-signed', item.mediaid],
    queryFn: async () => {
      await stopSession();

      const session = await entitlementService.startSession({ item, streamingProtocol });

      const widevine = session?.playInfo.drm?.widevine;
      const drm = widevine ? { widevine: { url: widevine.licenseAcquisitionURL } } : undefined;

      const timeline = session?.playInfo.timeline;
      const mainContentOffset = (timeline?.find(({ type }) => type === 'main')?.positionInMs || 0) / 1000;
      const contentDuration = (timeline?.find(({ type }) => type === 'end')?.positionInMs || 0) / 1000;

      return {
        mainContentOffset,
        contentDuration,
        playableItem: {
          ...item,
          sources: [
            {
              file: session.playInfo.url,
              type: streamingProtocol === 'dash' ? 'application/dash+xml' : 'application/vnd.apple.mpegurl',
              ...(drm ? { drm } : {}),
            },
          ],
        },
      };
    },
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    retry: false,
  });

  const getStartOffset = () => {
    if (watchHistory.startTime > 0) {
      // small compensation for when `item.duration` differs from the actual stream duration (i.e.: live programs that include a contentOffset)
      const actualDuration = contentDuration || item.duration;

      return watchHistory.startTime * (actualDuration / item.duration);
    }

    return mainContentOffset || 0;
  };

  return {
    playableItem,
    startOffset: getStartOffset(),
    isLoading,
    error,
  };
}
// /CUSTOMER
